/**
 * Portal Manta · ParamDfTab
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { SICRO_DESC } from '../../data/sicro-desc.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { V, S, fmtNum, sumBy, sigKey, pct, fmtBRL, isAmpliacao, AmpliBadge, OBRAS_ROD } from '../../globals.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

function ParamDfTab({lote}){
  const sig = sigKey(lote);
  const dm = lote.dados_manta||{};
  const cp = dm.compara_parametrico||{};
  const itens = (cp.itens||[]).filter(isAmpliacao);
  const od = OBRAS_ROD[sig]||{};
  const cf = CONSOL_FIN[sig]||{};
  const ext = parseFloat((lote.extensao||'').replace(/[^\d,.]/g,'').replace(',','.'))||0;
  const totSicroLote = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  // Catálogo de tipos de ampliação (linhas)
  const tiposAmpliacao = [
    {label:'Duplicação',     mef:'1.2.1',cods:['1.2.1'],   od:'duplicacoes',   field:'km',unid:'km',cor:'#1d4ed8'},
    {label:'Faixa Adic.',    mef:'1.2.5',cods:['1.2.5'],   od:'faixas',        field:'km',unid:'km',cor:'#ea580c'},
    {label:'Marginais',      mef:'1.2.2',cods:['1.2.2'],   od:'marginais',     field:'km',unid:'km',cor:'#16a34a'},
    {label:'Ciclovias',      mef:'1.2.7',cods:['1.2.7'],   od:'ciclovias',     field:'km',unid:'km',cor:'#db2777'},
    {label:'Contornos',      mef:null,   cods:[],          od:'contornos',     field:'km',unid:'km',cor:'#7c3aed'},
    {label:'OAEs',           mef:'1.2.9',cods:['1.2.9'],   od:'oaes',          field:'n', unid:'un',cor:'#7c3aed'},
    {label:'Passarelas',     mef:'1.2.10',cods:['1.2.10'], od:'passarelas',    field:'n', unid:'un',cor:'#0891b2'},
    {label:'Interseções',    mef:'1.2.3',cods:['1.2.3'],   od:['diamantes','trombetas','parclos','rotatorias'],field:'n',unid:'un',cor:'#f59e0b'},
  ];
  // Disciplinas (colunas)
  const discKeys = Object.keys(cf);
  const cores = {'PAVIMENTACAO':V.or,'TERRAPLENOS E CONTENCOES':V.bl,'DRENAGEM':V.gn,
                 'OAES':V.rxn||'#7c3aed','SINALIZACAO':V.am||'#d97706',
                 'ILUMINACAO':V.ind||'#0891b2','SERVICOS PRELIMINARES':V.gr||'#475569'};
  // Calcular: para cada (tipo_ampliação × disciplina) → R$/km, %, vol_dominante/km
  // Premissa: distribuir o R$ MEF do tipo proporcionalmente aos % do SICRO consolidado por disciplina (proxy)
  const totDisc = {};
  discKeys.forEach(d=>{ totDisc[d] = sumBy(cf[d]||[],'v'); });
  const matriz = useMemo(()=>{
    return tiposAmpliacao.map(t=>{
      const cats = Array.isArray(t.od)?t.od:[t.od];
      const qty = cats.reduce((acc,cat)=>acc + (od[cat]||[]).reduce((s,x)=>s+(x[t.field]||(t.field==='n'?1:0)),0),0);
      const itMef = t.mef ? itens.find(i=>t.cods.includes(i.codigo)) : null;
      const valTipo = itMef ? (itMef.pr_vias?.preco_total||0) : 0;
      const cells = {};
      discKeys.forEach(d=>{
        const pctDisc = totSicroLote ? totDisc[d]/totSicroLote : 0;
        const valCell = valTipo * pctDisc;
        const rkmCell = qty ? valCell/qty : 0;
        // volume dominante/km — agrega quantidades da disciplina
        const items = cf[d]||[];
        const totQty = sumBy(items,'q');
        const porU = {};
        items.forEach(it=>{const u=(it.u||'?').trim();porU[u]=(porU[u]||0)+(it.q||0);});
        const uDom = Object.entries(porU).filter(([u,q])=>q>0).sort((a,b)=>b[1]-a[1])[0];
        // estima vol/km na proporção do qty desta disciplina × % do tipo no SICRO
        const volKmCell = uDom && qty && totSicroLote ? (uDom[1]*pctDisc/qty) : 0;
        cells[d] = {valCell, rkmCell, pct:pctDisc*100, uDom, volKmCell};
      });
      return {...t, qty, valTipo, cells};
    });
  },[sig]);
  // Top 4 itens SICRO por disciplina (para a aba 6.4)
  const top4PorDisc = useMemo(()=>{
    const out = {};
    discKeys.forEach(d=>{
      const items = (cf[d]||[]).slice().sort((a,b)=>(b.v||0)-(a.v||0)).slice(0,4);
      out[d] = items;
    });
    return out;
  },[sig]);

  return(<div style={S.page}>
    <div style={S.h1}>6.2 · Paramétrico Manta · Matriz Ampliação × Disciplinas <AmpliBadge/></div>
    <div style={S.sub}>
      Linhas = tipos de obra de ampliação · Colunas = disciplinas (R$/km, %, vol/km) · {lote.nome}
    </div>

    {/* Quadro 1 · Matriz principal — R$/km */}
    <div style={S.h2}>Quadro 1 · R$/km — Ampliação × Disciplinas</div>
    <div style={{...S.card,overflow:'auto',marginBottom:14}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:120,position:'sticky',left:0,background:V.g0,zIndex:2}}>Tipo de Ampliação</th>
            <th style={TH}>Qtd</th>
            <th style={TH}>R$ MEF</th>
            {discKeys.map(d=>(
              <th key={d} style={{...TH,minWidth:100,background:cores[d]+'22',color:cores[d]}}>
                {d.replace('TERRAPLENOS E CONTENCOES','TERRAPL.').replace('SERVICOS PRELIMINARES','SV.PREL.').replace('PAVIMENTACAO','PAVIM.').replace('SINALIZACAO','SINAL.').replace('ILUMINACAO','ILUM.')}
              </th>
            ))}
            <th style={{...TH,fontWeight:700}}>R$/km TOT</th>
          </tr>
        </thead>
        <tbody>
          {matriz.map(t=>{
            const rkmTot = t.qty ? t.valTipo/t.qty : 0;
            return(<tr key={t.label} style={S.trLine}>
              <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1,borderLeft:'4px solid '+t.cor}}>{t.label}</td>
              <td style={TD}>{t.qty?fmtNum(t.qty,t.field==='n'?0:1)+' '+t.unid:'—'}</td>
              <td style={TD}>{t.valTipo?fmtBRL(t.valTipo,0):'—'}</td>
              {discKeys.map(d=>{
                const c = t.cells[d];
                return(<td key={d} style={{...TD,padding:'6px 4px',textAlign:'center',background:cores[d]+'08',fontFamily:'monospace'}}>
                  <div style={{fontWeight:700,color:V.br,fontSize:10}}>{c.rkmCell?fmtBRL(c.rkmCell,0):'—'}</div>
                  <div style={{fontSize:8.5,color:V.gr}}>{c.pct.toFixed(1)}%</div>
                </td>);
              })}
              <td style={{...TD,fontWeight:700,color:V.br}}>{rkmTot?fmtBRL(rkmTot,0):'—'}</td>
            </tr>);
          })}
        </tbody>
      </table>
    </div>

    {/* Quadro 2 · Volumes dominantes por disciplina por km de Ampliação */}
    <div style={S.h2}>Quadro 2 · Volume dominante / km — Ampliação × Disciplinas</div>
    <div style={{...S.card,overflow:'auto',marginBottom:14}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:120,position:'sticky',left:0,background:V.g0,zIndex:2}}>Tipo</th>
            <th style={TH}>Qtd</th>
            {discKeys.map(d=>(
              <th key={d} style={{...TH,minWidth:90,background:cores[d]+'22',color:cores[d]}}>
                {d.replace('TERRAPLENOS E CONTENCOES','TERRAPL.').replace('SERVICOS PRELIMINARES','SV.PREL.').replace('PAVIMENTACAO','PAVIM.').replace('SINALIZACAO','SINAL.').replace('ILUMINACAO','ILUM.')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matriz.map(t=>(<tr key={t.label} style={S.trLine}>
            <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1,borderLeft:'4px solid '+t.cor}}>{t.label}</td>
            <td style={TD}>{t.qty?fmtNum(t.qty,t.field==='n'?0:1)+' '+t.unid:'—'}</td>
            {discKeys.map(d=>{
              const c = t.cells[d];
              const vKm = c.volKmCell;
              return(<td key={d} style={{...TD,padding:'6px 4px',textAlign:'center',background:cores[d]+'08'}}>
                {vKm && c.uDom?(<>
                  <div style={{fontWeight:700,color:V.br,fontFamily:'monospace',fontSize:10}}>{fmtNum(vKm,1)}</div>
                  <div style={{fontSize:8.5,color:V.gr}}>{c.uDom[0]}/{t.unid}</div>
                </>):<span style={{color:V.gr}}>—</span>}
              </td>);
            })}
          </tr>))}
        </tbody>
      </table>
    </div>

    {/* Quadro 3 · Síntese R$ Total por Tipo */}
    <div style={S.h2}>Quadro 3 · Síntese — R$ MEF total por Tipo</div>
    <div style={{...S.card,overflow:'auto',marginBottom:14}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
        <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
          <th style={THl}>Tipo</th><th style={TH}>Qtd</th><th style={TH}>R$ MEF</th><th style={TH}>P.U. médio</th><th style={TH}>R$/km lote</th><th style={TH}>% do MEF</th>
        </tr></thead>
        <tbody>
          {(()=>{const totT=matriz.reduce((s,t)=>s+t.valTipo,0);return matriz.map(t=>(
            <tr key={t.label} style={S.trLine}>
              <td style={{...TDl,borderLeft:'4px solid '+t.cor}}><span style={{fontWeight:700,color:t.cor}}>{t.label}</span></td>
              <td style={TD}>{t.qty?fmtNum(t.qty,t.field==='n'?0:1)+' '+t.unid:'—'}</td>
              <td style={{...TD,fontWeight:700,color:V.br}}>{t.valTipo?fmtBRL(t.valTipo):'—'}</td>
              <td style={TD}>{t.qty&&t.valTipo?fmtBRL(t.valTipo/t.qty,0)+'/'+t.unid:'—'}</td>
              <td style={TD}>{ext&&t.valTipo?fmtBRL(t.valTipo/ext,0):'—'}</td>
              <td style={TD}>{totT&&t.valTipo?(t.valTipo/totT*100).toFixed(1)+'%':'—'}</td>
            </tr>));})()}
        </tbody>
      </table>
    </div>

    {/* Quadro 4 · TOP 4 ITENS SICRO POR DISCIPLINA — quadro único consolidado */}
    <div style={S.h2}>Quadro 4 · Top 4 itens SICRO por disciplina (quadro consolidado)</div>
    <div style={{...S.card,overflow:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:140,position:'sticky',left:0,background:V.g0,zIndex:2}}>Disciplina</th>
            <th style={{...THl,minWidth:200,background:'#fef3c7'}}>1º · TOP 1</th>
            <th style={{...THl,minWidth:200,background:'#fed7aa'}}>2º · TOP 2</th>
            <th style={{...THl,minWidth:200,background:'#fde68a'}}>3º · TOP 3</th>
            <th style={{...THl,minWidth:200,background:'#fef9c3'}}>4º · TOP 4</th>
            <th style={TH}>Σ Top 4</th>
            <th style={TH}>% disc.</th>
          </tr>
        </thead>
        <tbody>
          {discKeys.map(d=>{
            const top4 = top4PorDisc[d]||[];
            const sumTop4 = top4.reduce((s,it)=>s+(it.v||0),0);
            const totD = totDisc[d];
            return(<tr key={d} style={S.trLine}>
              <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1,borderLeft:'4px solid '+(cores[d]||V.bl)}}>{d}</td>
              {[0,1,2,3].map(i=>{
                const it = top4[i];
                if(!it) return(<td key={i} style={{...TD,padding:'8px 6px',color:V.gr,textAlign:'center'}}>—</td>);
                return(<td key={i} style={{...TD,padding:'8px 8px',textAlign:'left',whiteSpace:'normal',verticalAlign:'top'}}>
                  <div style={{fontFamily:'monospace',fontSize:9,color:V.bl,fontWeight:700}}>{it.c}</div>
                  <div style={{fontSize:9.5,color:V.br,marginTop:2,marginBottom:3,lineHeight:1.3}}>{(SICRO_DESC[it.c]||'').slice(0,80)}{(SICRO_DESC[it.c]||'').length>80?'…':''}</div>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:9,color:V.gr}}>
                    <span>{fmtNum(it.q,0)} {it.u}</span>
                    <span style={{fontWeight:700,color:V.br}}>{fmtBRL(it.v,0)}</span>
                  </div>
                </td>);
              })}
              <td style={{...TD,fontWeight:700,color:V.br,fontSize:10}}>{fmtBRL(sumTop4,0)}</td>
              <td style={{...TD,fontWeight:700}}>{totD?(sumTop4/totD*100).toFixed(1)+'%':'—'}</td>
            </tr>);
          })}
          <tr style={S.tfootRow}>
            <td colSpan={5} style={{padding:'8px 10px',fontSize:11,fontWeight:700,position:'sticky',left:0,background:V.g0,borderRight:'1px solid '+V.b1}}>TOTAL TOP 4 (concentração de Pareto)</td>
            <td style={{padding:'8px 6px',fontSize:11,fontWeight:700,textAlign:'right',color:V.br}}>{fmtBRL(discKeys.reduce((s,d)=>s+(top4PorDisc[d]||[]).reduce((a,b)=>a+(b.v||0),0),0),0)}</td>
            <td style={{padding:'8px 6px',fontSize:11,fontWeight:700,textAlign:'right'}}>—</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div style={{...S.cardP,marginTop:10,fontSize:10,color:V.gr,lineHeight:1.6}}>
      <b>Notas:</b> Os Quadros 1 e 2 distribuem o R$ MEF de cada tipo de ampliação proporcionalmente aos % das disciplinas no SICRO consolidado (proxy). Para cálculo preciso por tipo×disciplina, seria necessário cruzar cada item SICRO com seu tipo de obra (mapeamento manual ou via DNIT). 
      <br/>O Quadro 4 mostra os 4 SICRO de maior valor em cada disciplina — a soma deles representa a concentração de custo (Pareto) — útil para focar atenção nos itens críticos.
    </div>
  </div>);
}

export { ParamDfTab };
export default ParamDfTab;
